import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { resource as resourceMock, WorkLoad } from "@planzone/dto";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceResource } from "../../common/resource";
import { ChunkRequest, Request, Selector, State } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function upsertChunk(
  state: State,
  view: Request["view"],
  resourceUuid: string,
  chunkKey: string,
  next: (prev?: ChunkRequest) => ChunkRequest,
): State {
  const index = state.findIndex(
    (req) => req.view === view && req.resourceUuid === resourceUuid,
  );

  if (index === -1) {
    return [
      ...state,
      {
        view,
        resourceUuid,
        chunks: { [chunkKey]: next(undefined) },
      },
    ];
  }

  const entry = state[index];
  return [
    ...state.slice(0, index),
    {
      ...entry,
      chunks: {
        ...entry.chunks,
        [chunkKey]: next(entry.chunks[chunkKey]),
      },
    },
    ...state.slice(index + 1),
  ];
}

function handleRequest(
  state: State,
  filter: Boundaries["retrieveWorkLoads"]["request"],
): State {
  const chunkKey = filter.chunkKey;
  if (!chunkKey || !filter.resources) return state;
  return filter.resources.reduce(
    (next, resource) =>
      upsertChunk(next, filter.view, resource.uuid, chunkKey, (prev) => ({
        request: setRequestStateToPending(),
        workLoad: prev?.workLoad ?? null,
        isDirty: prev?.isDirty ?? false,
      })),
    state,
  );
}

function handleSuccess(
  state: State,
  { workLoads, filter }: Boundaries["retrieveWorkLoads"]["success"],
): State {
  const chunkKey = filter.chunkKey;
  if (!chunkKey || !filter.resources) return state;
  const byUuid = new Map<string, WorkLoad>(
    workLoads.map((wl) => [wl.uuid, wl]),
  );
  return filter.resources.reduce(
    (next, resource) =>
      upsertChunk(next, filter.view, resource.uuid, chunkKey, () => ({
        request: setRequestStateToSuccess(),
        workLoad: byUuid.get(resource.uuid) ?? null,
        isDirty: false,
      })),
    state,
  );
}

function handleFailure(
  state: State,
  filter: Boundaries["retrieveWorkLoads"]["failure"],
): State {
  const chunkKey = filter.chunkKey;
  if (!chunkKey || !filter.resources) return state;
  return filter.resources.reduce(
    (next, resource) =>
      upsertChunk(next, filter.view, resource.uuid, chunkKey, (prev) => ({
        request: setRequestStateToFailure(),
        workLoad: prev?.workLoad ?? null,
        isDirty: prev?.isDirty ?? false,
      })),
    state,
  );
}

function propagateEvent(state = initialState, event: Event): State {
  const dirties =
    event.type === events.editAllocationSucceeded ||
    event.type === events.removeAllocationSucceeded ||
    event.type === events.editTaskSucceeded ||
    event.type === events.editPersonalWeeklyCalendarSucceeded ||
    event.type === events.editCalendarHolidaysSucceeded ||
    event.type === events.removeCalendarHolidaysSucceeded ||
    event.type === events.assignCalendarHolidaysToResourceSucceeded ||
    event.type === events.assignPersonalCalendarHolidaysSucceeded ||
    event.type === events.createUnavailabilitySucceeded ||
    event.type === events.editUnavailabilitySucceeded ||
    event.type === events.removeUnavailabilitySucceeded;

  return state.map((req) => ({
    ...req,
    chunks: Object.fromEntries(
      Object.entries(req.chunks).map(([key, chunk]) => {
        const nextIsDirty = chunk.isDirty || dirties;
        if (!chunk.workLoad) {
          return [
            key,
            chunk.isDirty === nextIsDirty
              ? chunk
              : { ...chunk, isDirty: nextIsDirty },
          ];
        }
        const resource = reduceResource(
          {
            ...resourceMock,
            uuid: chunk.workLoad.uuid,
            name: chunk.workLoad.resourceName,
            profession: chunk.workLoad.resourceTitle || "",
          },
          event,
        );
        return [
          key,
          {
            ...chunk,
            isDirty: nextIsDirty,
            workLoad: {
              ...chunk.workLoad,
              uuid: resource.uuid,
              resourceName: resource.name,
              resourceTitle: resource.profession,
            },
          },
        ];
      }),
    ),
  }));
}

const reduceState = createStateReducer(
  initialState,
  {
    [events.retrieveWorkLoadsRequested]: handleRequest,
    [events.retrieveWorkLoadsSucceeded]: handleSuccess,
    [events.retrieveWorkLoadsFailed]: handleFailure,
  },
  propagateEvent,
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
